const DEFAULT_GA4_MEASUREMENT_ID = "G-ZP0GWZ33N5";
const DEFAULT_GOOGLE_ADS_ID = "AW-16890392694";
const DEFAULT_PLUSMAX_ADS_CONVERSION = "AW-16890392694/4xJRCJr316EaEPbg-_U-";

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_PATTERN = /(?:\+?\d[\d\s\-().]{8,}\d)/g;
const MAX_PARAM_STR_LEN = 120;

let initialized = false;

const hasWindow = () => typeof window !== "undefined";
const isDevHost = () => {
  if (!hasWindow()) return false;
  const host = String(window.location.hostname || "").toLowerCase();
  return host === "localhost" || host === "127.0.0.1";
};

const userOptedOut = () => {
  if (!hasWindow()) return false;
  try {
    const local = String(localStorage.getItem("cd_analytics_opt_out") || "").toLowerCase();
    const session = String(sessionStorage.getItem("cd_analytics_opt_out") || "").toLowerCase();
    if (local === "true" || session === "true" || local === "1" || session === "1") return true;
  } catch {}

  try {
    if (navigator?.globalPrivacyControl === true) return true;
  } catch {}

  try {
    const dnt = String(
      navigator?.doNotTrack ||
      window?.doNotTrack ||
      navigator?.msDoNotTrack ||
      ""
    ).toLowerCase();
    if (dnt === "1" || dnt === "yes") return true;
  } catch {}

  return false;
};

const shouldTrack = () => (
  hasWindow() &&
  !userOptedOut() &&
  (!isDevHost() || process.env.REACT_APP_ENABLE_ANALYTICS_IN_DEV === "true")
);

const stripPII = (value) => (
  String(value || "")
    .replace(EMAIL_PATTERN, "[redacted_email]")
    .replace(PHONE_PATTERN, "[redacted_phone]")
);

const sanitizeString = (value) => stripPII(value).slice(0, MAX_PARAM_STR_LEN);

const toFiniteNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const sanitizeValue = (value, depth = 0) => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "string") return sanitizeString(value);
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "boolean") return value;
  if (Array.isArray(value)) {
    if (depth >= 1) return undefined;
    return value
      .map((entry) => sanitizeValue(entry, depth + 1))
      .filter((entry) => entry !== undefined)
      .slice(0, 10);
  }
  if (typeof value === "object") {
    if (depth >= 1) return undefined;
    const out = {};
    Object.entries(value).forEach(([key, entry]) => {
      const sanitized = sanitizeValue(entry, depth + 1);
      if (sanitized !== undefined) {
        out[String(key).slice(0, 40)] = sanitized;
      }
    });
    return Object.keys(out).length ? out : undefined;
  }
  return undefined;
};

const sanitizeParams = (params = {}) => {
  const out = {};
  Object.entries(params).forEach(([key, value]) => {
    const sanitized = sanitizeValue(value);
    if (sanitized !== undefined) {
      out[String(key).slice(0, 40)] = sanitized;
    }
  });
  return out;
};

const normalizeEventName = (eventName) => {
  const raw = String(eventName || "").toLowerCase();
  let normalized = raw
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (!normalized) normalized = "custom_event";
  if (!/^[a-z]/.test(normalized)) normalized = `e_${normalized}`;
  return normalized.slice(0, 40);
};

const summarizeParamsForDb = (params = {}) => {
  const keys = Object.keys(params || {});
  if (!keys.length) return "";

  const picked = keys.slice(0, 6).reduce((acc, key) => {
    const value = params[key];
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      acc[key] = value;
    }
    return acc;
  }, {});

  try {
    const text = JSON.stringify(picked);
    return text.length > 160 ? `${text.slice(0, 160)}...` : text;
  } catch {
    return "";
  }
};

const mirrorEventToDb = (normalizedEvent, params = {}, options = {}) => {
  if (!hasWindow()) return;
  if (options?.skipDb === true) return;
  if (typeof window.__cdSendAnalytics !== "function") return;

  const summary = summarizeParamsForDb(params);
  const action = summary ? `${normalizedEvent} ${summary}` : normalizedEvent;

  try {
    window.__cdSendAnalytics("GA4_EVENT", action, String(new Date()), {
      skipGa: true,
      source: "ga4_mirror"
    });
  } catch {}
};

const ensureDataLayer = () => {
  if (!hasWindow()) return;
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }
};

const ensureGtagStub = () => {
  if (!hasWindow()) return;
  if (typeof window.gtag === "function") return;
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
};

const injectTagScript = (measurementId) => {
  if (!hasWindow() || !measurementId) return;
  if (document.getElementById("cd-ga4-loader")) return;

  const script = document.createElement("script");
  script.id = "cd-ga4-loader";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
};

const safeGtag = (...args) => {
  if (!shouldTrack()) return;
  ensureDataLayer();
  ensureGtagStub();
  try {
    window.gtag(...args);
  } catch {}
};

export const initializeAnalytics = () => {
  if (!hasWindow() || initialized || !shouldTrack()) return;

  const ga4Id = String(process.env.REACT_APP_GA4_MEASUREMENT_ID || DEFAULT_GA4_MEASUREMENT_ID).trim();
  const adsId = String(process.env.REACT_APP_GOOGLE_ADS_ID || DEFAULT_GOOGLE_ADS_ID).trim();
  const scriptId = ga4Id || adsId;

  ensureDataLayer();
  ensureGtagStub();
  injectTagScript(scriptId);

  safeGtag("js", new Date());
  if (ga4Id) {
    safeGtag("config", ga4Id, { send_page_view: false, anonymize_ip: true });
  }
  if (adsId) {
    safeGtag("config", adsId);
  }

  initialized = true;
};

export const trackEvent = (eventName, params = {}, options = {}) => {
  if (!shouldTrack()) return;
  initializeAnalytics();
  const normalizedEvent = normalizeEventName(eventName);
  const sanitized = sanitizeParams(params);
  safeGtag("event", normalizedEvent, sanitized);
  mirrorEventToDb(normalizedEvent, sanitized, options);
};

export const trackPageView = (pageTitle, params = {}, options = {}) => {
  if (!hasWindow()) return;
  trackEvent("page_view", {
    page_title: pageTitle || "CASADATA",
    page_location: window.location.href,
    page_path: `${window.location.pathname}${window.location.search || ""}`,
    ...params
  }, options);
};

export const trackAdsConversion = (sendTo = DEFAULT_PLUSMAX_ADS_CONVERSION, params = {}) => {
  initializeAnalytics();
  safeGtag("event", "conversion", sanitizeParams({ send_to: sendTo, ...params }));
};

export const trackBeginCheckout = ({
  plan = "PlusMAX",
  billing = "monthly",
  value,
  currency = "USD",
  source = ""
} = {}, options = {}) => {
  trackEvent("begin_checkout", {
    plan_tier: plan,
    billing_cycle: billing,
    value: toFiniteNumber(value),
    currency,
    source
  }, options);
};

export const trackAddPaymentInfo = ({
  plan = "PlusMAX",
  billing = "",
  method = "",
  value,
  currency = "USD"
} = {}, options = {}) => {
  trackEvent("add_payment_info", {
    plan_tier: plan,
    billing_cycle: billing,
    payment_method: method,
    value: toFiniteNumber(value),
    currency
  }, options);
};

export const trackPurchase = ({
  transactionId = "",
  plan = "PlusMAX",
  billing = "",
  method = "",
  value,
  currency = "USD"
} = {}, options = {}) => {
  trackEvent("purchase", {
    transaction_id: transactionId,
    plan_tier: plan,
    billing_cycle: billing,
    payment_method: method,
    value: toFiniteNumber(value),
    currency
  }, options);
};

export const trackSearchInteraction = ({
  searchType = "map_polygon",
  resultCount,
  isTrialSearch = false
} = {}, options = {}) => {
  trackEvent("search", {
    search_term: searchType,
    search_type: searchType,
    result_count: toFiniteNumber(resultCount),
    is_trial_search: Boolean(isTrialSearch)
  }, options);
};

export const setAnalyticsUser = ({
  userId = "",
  planTier = "",
  isTrial = false,
  language = ""
} = {}) => {
  initializeAnalytics();
  safeGtag("set", "user_id", userId ? String(userId) : null);
  safeGtag("set", "user_properties", sanitizeParams({
    plan_tier: planTier,
    trial_user: Boolean(isTrial),
    language
  }));
};

export const clearAnalyticsUser = () => {
  initializeAnalytics();
  safeGtag("set", "user_id", null);
  safeGtag("set", "user_properties", sanitizeParams({
    plan_tier: "anonymous",
    trial_user: false
  }));
};

export const trackLegacyAnalytics = ({ page = "", action = "", date = "" } = {}, options = {}) => {
  trackEvent("legacy_analytics", {
    legacy_page: sanitizeString(page),
    legacy_action: sanitizeString(action),
    legacy_date: sanitizeString(date)
  }, options);
};
